import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import MyCoursesPage from "@/pages/MyCoursesPage";
import WatchPage from "@/pages/WatchPage";
import ProfilePage from "@/pages/ProfilePage";
import HelpPage from "@/pages/HelpPage";
import BooksPage from "@/pages/BooksPage";
import PrintedBooksPage from "@/pages/PrintedBooksPage";
import EbooksPage from "@/pages/EbooksPage";
import FreeEbooksPage from "@/pages/FreeEbooksPage";
import FreeClassesPage from "@/pages/FreeClassesPage";
import MockTestsPage from "@/pages/MockTestsPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminChapters from "@/pages/admin/AdminChapters";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminInstructors from "@/pages/admin/AdminInstructors";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminContent from "@/pages/admin/AdminContent";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminNotes from "@/pages/admin/AdminNotes";
import AdminPrintedBooks from "@/pages/admin/AdminPrintedBooks";
import AdminEbooks from "@/pages/admin/AdminEbooks";
import AdminFreeEbooks from "@/pages/admin/AdminFreeEbooks";
import AdminLiveSessions from "@/pages/admin/AdminLiveSessions";
import AdminDataMigration from "@/pages/admin/AdminDataMigration";
import AdminQuizzes from "@/pages/admin/AdminQuizzes";
import AdminLive from "@/pages/admin/AdminLive";
import AdminFreeClasses from "@/pages/admin/AdminFreeClasses";
import AdminMockTests from "@/pages/admin/AdminMockTests";

import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import Sidebar from "@/components/Sidebar";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function UserLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white max-w-lg mx-auto relative" data-testid="user-layout">
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {children}
      <BottomNav />
      <PWAInstallPrompt />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading) {
      const splash = document.getElementById("splash-screen");
      if (splash) {
        splash.style.opacity = "0";
        setTimeout(() => splash.remove(), 300);
      }
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0284C7]">
        <div className="text-center text-white">
          <img
            src="https://customer-assets.emergentagent.com/job_1791f9d8-ffce-4632-931c-e01b5c5c94c3/artifacts/sbeyyf6q_1000003487.png"
            alt="Logo"
            className="w-20 h-20 mx-auto mb-4"
          />
          <p className="text-sm font-bold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? (user.role === 'admin' ? <Navigate to="/admin" replace /> : <Navigate to="/" replace />) : <LoginPage />} />

      {/* User Pages */}
      <Route path="/" element={<ProtectedRoute><UserLayout><HomePage /></UserLayout></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><UserLayout><CoursesPage /></UserLayout></ProtectedRoute>} />
      <Route path="/books" element={<ProtectedRoute><UserLayout><BooksPage /></UserLayout></ProtectedRoute>} />
      <Route path="/printed-books" element={<ProtectedRoute><UserLayout><PrintedBooksPage /></UserLayout></ProtectedRoute>} />
      <Route path="/ebooks" element={<ProtectedRoute><UserLayout><EbooksPage /></UserLayout></ProtectedRoute>} />
      <Route path="/free-ebooks" element={<ProtectedRoute><UserLayout><FreeEbooksPage /></UserLayout></ProtectedRoute>} />
      <Route path="/courses/:id" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
      <Route path="/my-courses" element={<ProtectedRoute><UserLayout><MyCoursesPage /></UserLayout></ProtectedRoute>} />
      <Route path="/watch/:courseId" element={<ProtectedRoute><WatchPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><UserLayout><ProfilePage /></UserLayout></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><UserLayout><HelpPage /></UserLayout></ProtectedRoute>} />
      <Route path="/free-classes" element={<ProtectedRoute><UserLayout><FreeClassesPage /></UserLayout></ProtectedRoute>} />
      <Route path="/mock-tests" element={<ProtectedRoute><UserLayout><MockTestsPage /></UserLayout></ProtectedRoute>} />

      {/* Admin Pages */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin/courses" element={<AdminRoute><AdminCourses /></AdminRoute>} />
      <Route path="/admin/courses/:courseId/chapters" element={<AdminRoute><AdminChapters /></AdminRoute>} />
      <Route path="/admin/chapters/:chapterId/videos" element={<AdminRoute><AdminVideos /></AdminRoute>} />
      <Route path="/admin/courses/:courseId/live-sessions" element={<AdminRoute><AdminLiveSessions /></AdminRoute>} />
      <Route path="/admin/courses/:courseId/quizzes" element={<AdminRoute><AdminQuizzes /></AdminRoute>} />
      <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
      <Route path="/admin/content" element={<AdminRoute><AdminContent /></AdminRoute>} />
      <Route path="/admin/banners" element={<AdminRoute><AdminBanners /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/instructors" element={<AdminRoute><AdminInstructors /></AdminRoute>} />
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
      <Route path="/admin/notes" element={<AdminRoute><AdminNotes /></AdminRoute>} />
      <Route path="/admin/printed-books" element={<AdminRoute><AdminPrintedBooks /></AdminRoute>} />
      <Route path="/admin/ebooks" element={<AdminRoute><AdminEbooks /></AdminRoute>} />
      <Route path="/admin/free-ebooks" element={<AdminRoute><AdminFreeEbooks /></AdminRoute>} />
      <Route path="/admin/free-classes" element={<AdminRoute><AdminFreeClasses /></AdminRoute>} />
      <Route path="/admin/mock-tests" element={<AdminRoute><AdminMockTests /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/data-migration" element={<AdminRoute><AdminDataMigration /></AdminRoute>} />
      <Route path="/admin/live" element={<AdminRoute><AdminLive /></AdminRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App no-select" onContextMenu={(e) => e.preventDefault()}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
